import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Table,
  Button,
  Badge,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import { FetchAPI } from "../../../../helpers/APIdata/fetchAPI";
import { HandleDateTimeAgo } from "../../../../Components/Common/HandleDateTimeAgo";

// Settings > User Profile > My Devices.
// Lists the devices recognised for the logged-in user (TraceTail browser
// fingerprints captured at login) and lets them rename or remove one.
const MyDevicesSettings = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadDevices = async () => {
    setLoading(true);
    try {
      const res = await FetchAPI("/userdevice/list", "GET");
      setDevices(res && res.userdevice_list ? res.userdevice_list : []);
    } catch (_) {
      setDevices([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadDevices();
  }, []);

  const handleRename = async (device) => {
    const current = device.UserDeviceFingerprint_DeviceName || "";
    const next = window.prompt("Rename this device", current);
    if (next == null) return; // cancelled
    const trimmed = next.trim();
    if (!trimmed || trimmed === current) return;

    const res = await FetchAPI(
      "/userdevice/update/" + device.UserDeviceFingerprint_Id,
      "PUT",
      { DeviceName: trimmed }
    );
    if (res && res.status === 200) {
      toast.success("Device renamed");
      loadDevices();
    } else {
      toast.error((res && res.message) || "Could not rename device");
    }
  };

  const handleRemove = async (device) => {
    const label = device.UserDeviceFingerprint_DeviceName || "this device";
    if (!window.confirm(`Remove ${label}? You'll be asked to name it again next time you sign in on it.`)) {
      return;
    }
    const res = await FetchAPI(
      "/userdevice/delete/" + device.UserDeviceFingerprint_Id,
      "DELETE"
    );
    if (res && res.status === 200) {
      toast.success("Device removed");
      loadDevices();
    } else {
      toast.error((res && res.message) || "Could not remove device");
    }
  };

  const fmtDate = (value) => {
    if (!value) return "-";
    const d = new Date(value.replace(" ", "T"));
    return isNaN(d.getTime()) ? value : d.toLocaleString();
  };

  const fmtAgo = (value) =>
    value ? HandleDateTimeAgo(String(value).replace(" ", "T")) : null;

  return (
    <Card>
      <CardBody>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h5 className="mb-1">My Devices</h5>
            <p className="text-muted mb-0">
              Devices we recognise when you sign in. Remove any you don't
              recognise.
            </p>
          </div>
          <Button color="light" size="sm" onClick={loadDevices} disabled={loading}>
            <i className="ri-refresh-line align-middle me-1" />
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : devices.length === 0 ? (
          <div className="text-center text-muted py-5">
            No devices recorded yet.
          </div>
        ) : (
          <div className="table-responsive">
            <Table className="align-middle table-nowrap mb-0">
              <thead className="table-light">
                <tr>
                  <th>Device</th>
                  <th>Last login</th>
                  <th>Last active</th>
                  <th>Last IP</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d) => (
                  <tr key={d.UserDeviceFingerprint_Id}>
                    <td>
                      <i className="ri-computer-line align-middle me-2 text-muted" />
                      {d.UserDeviceFingerprint_DeviceName || (
                        <span className="text-muted fst-italic">Unnamed device</span>
                      )}
                    </td>
                    <td>{fmtDate(d.UserDeviceFingerprint_LastLoginDateTime)}</td>
                    <td>
                      <div>{fmtDate(d.UserDeviceFingerprint_LastActiveDateTime)}</div>
                      {fmtAgo(d.UserDeviceFingerprint_LastActiveDateTime) && (
                        <Badge color="light" className="text-muted mt-1">
                          {fmtAgo(d.UserDeviceFingerprint_LastActiveDateTime)}
                        </Badge>
                      )}
                    </td>
                    <td>{d.UserDeviceFingerprint_LastSeenIpAddress || "-"}</td>
                    <td>
                      {Number(d.UserDeviceFingerprint_TrustedFlag) === 1 ? (
                        <Badge color="success">Trusted</Badge>
                      ) : (
                        <Badge color="warning">Unconfirmed</Badge>
                      )}
                    </td>
                    <td className="text-end">
                      <Button
                        color="light"
                        size="sm"
                        className="me-2"
                        onClick={() => handleRename(d)}
                      >
                        Rename
                      </Button>
                      <Button
                        color="danger"
                        size="sm"
                        outline
                        onClick={() => handleRemove(d)}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default MyDevicesSettings;
